'use client';

interface ErrorMessageProps {
  message: string;
  onDismiss?: () => void;
  type?: 'error' | 'warning' | 'info';
  retryAction?: {
    label: string;
    onClick: () => void;
  };
}

export default function ErrorMessage({ 
  message, 
  onDismiss, 
  type = 'error',
  retryAction 
}: ErrorMessageProps) {
  const styles = {
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-400',
      text: 'text-red-800',
      button: 'text-red-400 hover:text-red-600',
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      icon: 'text-yellow-400',
      text: 'text-yellow-800',
      button: 'text-yellow-400 hover:text-yellow-600',
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-400',
      text: 'text-blue-800',
      button: 'text-blue-400 hover:text-blue-600',
    },
  };

  const style = styles[type];

  // Check if it's a rate limit error
  const isRateLimit = message.toLowerCase().includes('rate limit') || message.toLowerCase().includes('too many requests');
  const isNetworkError = message.toLowerCase().includes('network') || message.toLowerCase().includes('fetch');

  return (
    <div className={`${style.bg} border ${style.border} rounded-md p-4 mb-4`}>
      <div className="flex justify-between items-start">
        <div className="flex flex-1">
          <div className="flex-shrink-0">
            {isRateLimit ? (
              <svg className={`h-5 w-5 ${style.icon}`} viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className={`h-5 w-5 ${style.icon}`} viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div className="ml-3 flex-1">
            <p className={`text-sm ${style.text} font-medium`}>
              {isRateLimit ? 'Rate Limit Exceeded' : isNetworkError ? 'Network Error' : 'Error'}
            </p>
            <p className={`text-sm ${style.text} mt-1`}>{message}</p>
            {isRateLimit && (
              <p className="text-xs text-gray-600 mt-2">
                Please wait a moment before trying again.
              </p>
            )}
            {isNetworkError && (
              <p className="text-xs text-gray-600 mt-2">
                Check your connection and try again.
              </p>
            )}
            {retryAction && (
              <button
                onClick={retryAction.onClick}
                className={`mt-3 px-3 py-1 text-xs ${style.text} ${style.bg} border ${style.border} rounded hover:opacity-80 transition-opacity`}
              >
                {retryAction.label}
              </button>
            )}
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`ml-4 flex-shrink-0 ${style.button}`}
            aria-label="Dismiss"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

